import React from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import MetricCard from './MetricCard';
import LoadingCard from './LoadingCard';
import EmptyState from './EmptyState';
import type { SurveyData } from '../types';

interface SurveyMetricsSectionProps {
  surveyData: SurveyData | null;
  loading: boolean;
  error: string | null;
  onNavigateToSurveys: () => void;
  onRetry: () => void;
}

const SurveyMetricsSection: React.FC<SurveyMetricsSectionProps> = ({
  surveyData,
  loading,
  error,
  onNavigateToSurveys,
  onRetry,
}) => {
  const { t } = useTranslation();
  const hasSurveyData = surveyData !== null;
  // CO-815 review 3 finding 1: under 5 responses the server withholds the breakdown and score, so the cards
  // must not render them as zeros (an "eNPS 0" reads as a real, neutral result).
  const withheld = surveyData?.insufficientData === true;

  const getNpsTrend = (): 'up' | 'down' | 'stable' => {
    if (!surveyData) return 'stable';
    if (surveyData.nps > 50) return 'up';
    if (surveyData.nps >= 0) return 'stable';
    return 'down';
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-medium text-foreground">Survey Metrics</h3>
        <Button variant="ghost" size="sm" onClick={onNavigateToSurveys}>
          View Surveys
          <AppIcon name="ArrowRight" size={14} className="ms-1" />
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, index) => (
            <LoadingCard key={`survey-loading-${index}`} />
          ))}
        </div>
      ) : error && !hasSurveyData ? (
        <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-4 text-sm text-red-800 dark:text-red-200 mb-4">
          {error}
        </div>
      ) : hasSurveyData ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MetricCard
            title="Survey Responses"
            value={surveyData.totalResponses || 0}
            icon="ClipboardList"
            iconColor="text-blue-500"
            trend={withheld ? undefined : { type: 'up' }}
            subtitle={
              withheld
                ? t('performance.surveyInsufficientData')
                : `${surveyData.promoters || 0} promoters, ${surveyData.detractors || 0} detractors`
            }
          />
          <MetricCard
            title="Net Promoter Score"
            value={withheld ? '—' : surveyData.nps || 0}
            icon="BarChart3"
            iconColor="text-emerald-500"
            trend={withheld ? undefined : { type: getNpsTrend() }}
            subtitle={
              withheld
                ? t('performance.surveyInsufficientData')
                : `${surveyData.promoters || 0} promoters, ${surveyData.passives || 0} passives`
            }
          />
        </div>
      ) : (
        <EmptyState
          icon="ClipboardList"
          title="No survey data available"
          description="Survey participation and feedback data is not currently available."
          actionLabel="Refresh Surveys"
          onAction={onRetry}
          variant="surveys"
        />
      )}
    </div>
  );
};

export default SurveyMetricsSection;
